<template>
  <div class="enlaces-view">
    <header class="view-header">
      <div class="view-header__text">
        <h1 class="view-header__title">Enlaces</h1>
        <p class="view-header__sub">Los enlaces que se muestran en tu página pública de Sartor.</p>
      </div>

      <create ref="create" @reload="fetchSocials" />
    </header>

    <draggable
      v-model="socials"
      handle=".drag-handle"
      class="socials-list"
      @end="persistOrder"
      >
      <div
        v-for="social in socials"
        :key="social.id"
        class="social-row"
        >
        <span
          class="drag-handle social-row__grip"
          title="Arrastrar para ordenar"
          >
          <hero-icon :icon="Bars3Icon" :size="20" />
        </span>

        <div class="social-row__avatar">
          <img
            v-if="social.imageUrl"
            :src="imageUrl(social.imageUrl)"
            alt=""
            >
          <span v-else>{{ social.name.charAt(0) }}</span>
        </div>

        <div class="social-row__info">
          <div class="social-row__name">{{ social.name }}</div>
          <div
            v-if="social.description"
            class="social-row__desc"
            >
            {{ social.description }}
          </div>
        </div>

        <div class="social-row__actions">
          <v-btn
            depressed
            color="primary"
            class="rounded-lg"
            :href="social.link"
            target="_blank"
            >
            {{ social.buttonTag || 'ABRIR' }}
          </v-btn>

          <v-menu offset-y left min-width="160">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-on="on"
                v-bind="attrs"
                title="Más opciones"
                >
                <hero-icon :icon="EllipsisVerticalIcon" :size="20" />
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="openEdit(social)">
                <v-list-item-icon class="mr-3">
                  <hero-icon :icon="PencilSquareIcon" :size="18" />
                </v-list-item-icon>
                <v-list-item-title>Editar</v-list-item-title>
              </v-list-item>

              <v-list-item @click="openDelete(social)">
                <v-list-item-icon class="mr-3">
                  <hero-icon :icon="TrashIcon" :size="18" />
                </v-list-item-icon>
                <v-list-item-title>Eliminar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </draggable>

    <div
      v-if="!socials.length"
      class="socials-empty"
      >
      Todavía no hay enlaces. Agregá el primero con “+ Nuevo enlace”.
    </div>

    <edit
      v-model="editOpen"
      :social="editTarget"
      @reload="fetchSocials"
      />

    <delete
      v-model="deleteOpen"
      :id="deleteTarget ? deleteTarget.id : null"
      @reload="fetchSocials"
      />

    <v-btn
      v-if="$vuetify.breakpoint.smAndDown"
      fab
      fixed
      bottom
      right
      color="accent"
      class="enlaces-fab"
      aria-label="Nuevo enlace"
      @click="$refs.create.open()"
      >
      <hero-icon :icon="PlusIcon" :size="24" />
    </v-btn>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

import { ReorderSocials } from '@/graphql/mutations/social'
import { Socials } from '@/graphql/queries/social'

import Create from '@/components/settings/socials/Create'
import Delete from '@/components/settings/socials/Delete'
import Edit from '@/components/settings/socials/Edit'
import HeroIcon from '@/components/shared/HeroIcon'
import Bars3Icon from 'heroicons/24/outline/bars-3.svg'
import EllipsisVerticalIcon from 'heroicons/24/outline/ellipsis-vertical.svg'
import PencilSquareIcon from 'heroicons/24/outline/pencil-square.svg'
import TrashIcon from 'heroicons/24/outline/trash.svg'
import PlusIcon from 'heroicons/24/outline/plus.svg'

export default {
  data: () => ({
    Bars3Icon,
    EllipsisVerticalIcon,
    PencilSquareIcon,
    TrashIcon,
    PlusIcon,
    socials: [],
    valid: true,
    editOpen: false,
    deleteOpen: false,
    editTarget: {},
    deleteTarget: null
  }),

  created () {
    this.fetchSocials('cache-first')
  },

  methods: {
    imageUrl (url) {
      return process.env.VUE_APP_RAILS_URL + url
    },

    // initial load is cache-first; @reload callers (create/edit/delete/reorder)
    // pass no arg -> 'no-cache' so post-mutation lists are always fresh
    fetchSocials (policy = 'no-cache') {
      this.$apollo.query({
        query: Socials,
        fetchPolicy: policy
      }).then ( res => {
        this.socials = res.data.socials
      })
    },

    openEdit (social) {
      this.editTarget = social
      this.editOpen = true
    },

    openDelete (social) {
      this.deleteTarget = social
      this.deleteOpen = true
    },

    // persist the new order after a drag finishes (vuedraggable has already
    // reordered this.socials via v-model); re-sync from the server on failure
    persistOrder () {
      this.$apollo.mutate({
        mutation: ReorderSocials,
        variables: {
          input: { ids: this.socials.map(s => s.id) }
        }
      }).catch(() => this.fetchSocials())
    }
  },

  components: {
    draggable,
    Create,
    Delete,
    Edit,
    HeroIcon
  }
}
</script>

<style scoped>
/* layout (centering/cap/padding) comes from the global .app-content in App.vue */

/* ── reusable view header (the base pattern for other views) ── */
.view-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}
.view-header__title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a1814;
  margin: 0;
}
.view-header__sub {
  font-size: 13.5px;
  color: #756f64;
  margin: 5px 0 0;
}

/* echoes the public /enlaces link-card layout, in the CRM's Sartor-branded
   language (warm ink + brick-red accents, not the full editorial system) */
.socials-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.social-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 12px 16px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}
.social-row:hover {
  border-color: #dcd4c6;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}

.social-row__grip {
  flex: none;
  display: flex;
  cursor: grab;
  color: #c4bcae;
}
.social-row__grip:active { cursor: grabbing; }

.social-row__avatar {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 20px;
}
.social-row__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.social-row__info {
  flex: 1;
  min-width: 0;
}
.social-row__name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1814;
}
.social-row__desc {
  font-size: 13px;
  color: #756f64;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.social-row__actions {
  flex: none;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.socials-empty {
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  color: #a39a8c;
  font-size: 14px;
}

.enlaces-fab { z-index: 6; }
.enlaces-fab .hero-icon { color: #fff; }

/* mobile */
@media (max-width: 600px) {
  .social-row { gap: 10px; padding: 10px 12px; }
  .social-row__desc { display: none; }
  .social-row__actions { gap: 4px; }
}
</style>
